<template>
  <div>
    <v-dialog v-model="dialog" scrollable hide-overlay max-width="800px">
      <v-card
        class="rounded-lg textLight--text"
        style="height: fit-content"
        :style="$vuetify.theme.dark ? '' : 'background-color: #f5f5f5'"
      >
        <v-container fluid>
          <v-row class="pb-2 pa-5">
            <v-col cols="auto">
              <div
                class="font-weight-medium text-h5 text-start textLight--text"
              >
                {{ $t("course.assignedCourses.assignCourse") }}
              </div>
            </v-col>
          </v-row>

          <v-row>
            <v-col order="2" order-md="1">
              <ValidationObserver ref="observer">
                <v-row class="pa-5">
                  <v-col>
                    <v-row>
                      <v-col cols="3" class="d-flex align-center">
                        <p
                          class="ma-0 text-start font-weight-thin text-truncate"
                        >
                          {{ $t("user.registration.organization") }}
                        </p></v-col
                      >
                      <v-col cols="9">
                        <div class="d-flex justify-space-between align-center">
                          <validation-provider
                            v-slot="{ errors }"
                            rules="required"
                            style="width: 100%"
                          >
                            <v-autocomplete
                              hide-details
                              @click="registerErr = null"
                              v-model="organization"
                              :error-messages="errors"
                              dense
                              :items="organizationsData"
                              item-value="_id"
                              flat
                              solo
                              outlined
                              :label="
                                $t('user.registration.selectOrganization')
                              "
                              class="rounded-lg"
                              :filter="customLocalisedAutoCompleteFilter"
                              :readonly="employeeOrganization ? true : false"
                            >
                              <template slot="selection" slot-scope="data">
                                {{ getLocalisedValue(data.item.name) }}
                              </template>

                              <template v-slot:item="{ item }">
                                <div
                                  class="d-flex flex-column text-start"
                                  style="width: 100%"
                                >
                                  {{ getLocalisedValue(item.name) }}
                                  <v-divider></v-divider>
                                </div>
                              </template>
                            </v-autocomplete>
                          </validation-provider>

                          <template
                            v-if="
                              havePermissionCreateOrganization &&
                              !employeeOrganization
                            "
                          >
                            <v-btn
                              v-if="!hideAddBtn"
                              depressed
                              outlined
                              min-width="0"
                              class="pa-3 ms-3"
                              @click="
                                () => {
                                  createOrUpdateOrganization = true;
                                  hideAddBtn = true;
                                }
                              "
                            >
                              <v-icon>mdi-plus</v-icon>
                            </v-btn>

                            <v-btn
                              v-if="hideAddBtn"
                              depressed
                              outlined
                              min-width="0"
                              class="pa-3 ms-3"
                              @click="
                                () => {
                                  createOrUpdateOrganization = false;
                                  hideAddBtn = false;
                                }
                              "
                            >
                              <v-icon>mdi-minus</v-icon>
                            </v-btn>
                          </template>
                        </div>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="3" class="d-flex align-center">
                        <p
                          class="ma-0 text-start font-weight-thin text-truncate"
                        >
                          {{ $t("course.course") }}
                        </p></v-col
                      >
                      <v-col cols="9">
                        <validation-provider
                          v-slot="{ errors }"
                          rules="required"
                        >
                          <v-autocomplete
                            hide-details
                            :error-messages="errors"
                            v-model="course"
                            outlined
                            @input="setCourseDuration"
                            @click="registerErr = null"
                            dense
                            :items="coursesData"
                            item-value="_id"
                            flat
                            solo
                            :label="$t('user.registration.selectCourse')"
                            class="rounded-lg"
                            :filter="customLocalisedAutoCompleteFilter"
                          >
                            <template slot="selection" slot-scope="data">
                              {{ getLocalisedValue(data.item.title) }}
                            </template>
                            <template v-slot:item="{ item }">
                              <div
                                class="d-flex flex-column text-start"
                                style="width: 100%"
                              >
                                {{ getLocalisedValue(item.title) }}
                                <v-divider></v-divider>
                              </div>
                            </template>
                          </v-autocomplete>
                        </validation-provider>
                      </v-col>
                    </v-row>

                    <v-row v-if="isCourseOffline">
                      <v-col cols="3" class="d-flex align-center">
                        <p
                          class="ma-0 text-start font-weight-thin text-truncate"
                        >
                          {{ $t("course.trainer") }}
                        </p></v-col
                      >
                      <v-col cols="9">
                        <validation-provider
                          v-slot="{ errors }"
                          rules="required"
                        >
                          <v-autocomplete
                            hide-details
                            :error-messages="errors"
                            v-model="trainer"
                            outlined
                            @click="registerErr = null"
                            dense
                            :items="trainers"
                            item-value="_id"
                            :item-text="getEmployeeText"
                            flat
                            solo
                            :label="$t('user.trainers.selectTrainer')"
                            class="rounded-lg"
                            :filter="customLocalisedAutoCompleteFilter"
                          >
                            <template v-slot:item="{ item }">
                              <div class="d-flex justify-start">
                                {{
                                  `${item.name || ""} ${item.lastName || ""} ${
                                    item.email ? "(" + item.email + ")" : ""
                                  }`
                                }}
                              </div>
                            </template>
                          </v-autocomplete>
                        </validation-provider>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="3" class="d-flex align-center">
                        <p
                          class="ma-0 text-start font-weight-thin text-truncate"
                        >
                          {{ $t("course.courseDuration") }}
                        </p></v-col
                      >
                      <v-col cols="9">
                        <v-row>
                          <v-col cols="12" sm="5" class="pe-sm-0">
                            <v-menu
                              v-model="startDateMenu"
                              :close-on-content-click="false"
                              transition="scale-transition"
                              offset-y
                            >
                              <template v-slot:activator="{ on, attrs }">
                                <validation-provider
                                  v-slot="{ errors }"
                                  rules="required"
                                >
                                  <v-text-field
                                    hide-details
                                    v-model="formattedStartDate"
                                    :error-messages="errors"
                                    placeholder="DD/MM/YYYY"
                                    class="rounded-lg"
                                    outlined
                                    dense
                                    readonly
                                    v-bind="attrs"
                                    v-on="on"
                                  >
                                    <v-icon slot="append" color="textLight">
                                      mdi-calendar-blank-outline
                                    </v-icon></v-text-field
                                  >
                                </validation-provider>
                              </template>
                              <v-date-picker
                                color="primary"
                                v-model="startDate"
                                no-title
                                @input="startDateMenu = false"
                              ></v-date-picker>
                            </v-menu>
                          </v-col>
                          <v-col
                            cols="12"
                            sm="2"
                            class="px-sm-0 d-flex align-center"
                            v-if="$vuetify.breakpoint.smAndUp"
                          >
                            <div style="width: 100%" class="font-weight-thin">
                              {{ $t("user.registration.to") }}
                            </div>
                          </v-col>
                          <v-col cols="12" sm="5" class="ps-sm-0">
                            <validation-provider
                              v-slot="{ errors }"
                              rules="required"
                            >
                              <v-text-field
                                hide-details
                                :error-messages="errors"
                                class="rounded-lg"
                                placeholder="DD/MM/YYYY"
                                v-model="endDate"
                                outlined
                                dense
                                readonly
                              >
                                <v-icon slot="append" color="textLight">
                                  mdi-calendar-blank-outline
                                </v-icon></v-text-field
                              >
                            </validation-provider>
                          </v-col>
                        </v-row>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="3" class="d-flex align-center">
                        <p class="ma-0 text-start font-weight-thin">
                          {{ $t("course.courseDetails.certificateValidity") }}
                        </p>
                      </v-col>
                      <v-col cols="9">
                        <validation-provider
                          v-slot="{ errors }"
                          rules="required"
                        >
                          <v-text-field
                            hide-details
                            type="number"
                            min="0"
                            v-model.number="certificateValidity"
                            :error-messages="errors"
                            class="rounded-lg text-field-prefix-opacity"
                            outlined
                            dense
                            :prefix="certificateValidityPrefix"
                          >
                          </v-text-field>
                        </validation-provider>
                      </v-col>
                    </v-row>

                    <!--                    <v-row>
                      <v-col cols="3" class="d-flex align-center">
                        <p
                          class="ma-0 text-start font-weight-thin text-truncate"
                        >
                          {{ $t("user.registration.courseRate") }}
                        </p>
                      </v-col>

                      <v-col cols="9">
                        &lt;!&ndash; <validation-provider v-slot="{ errors }" rules="required"> &ndash;&gt;
                        <v-text-field
                          color="#9e9e9e"
                          v-model.number="courseRate"
                          @click="registerErr = null"
                          :prefix="defaultPrice + '|'"
                          class="rounded-lg"
                          type="number"
                          dense
                          hide-details
                          outlined
                          :min="0"
                          @keypress="
                            numberInputEventFilter({
                              event: $event,
                              isFloat: true,
                            })
                          "
                          @paste="
                            numberInputEventFilter({
                              event: $event,
                              isFloat: true,
                            })
                          "
                        ></v-text-field>
                        &lt;!&ndash; </validation-provider > &ndash;&gt;
                      </v-col>
                    </v-row>-->

                    <v-row>
                      <v-col cols="3" class="d-flex align-center">
                        <p
                          class="ma-0 text-start font-weight-thin text-truncate"
                        >
                          {{ $t("user.registration.remarks") }}
                        </p>
                      </v-col>
                      <v-col cols="9">
                        <v-textarea
                          v-model="remarks"
                          outlined
                          @click="registerErr = null"
                          class="rounded-lg"
                          dense
                          hide-details
                        >
                        </v-textarea>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12">
                        <v-checkbox
                          class="ma-0"
                          v-model="isCourseOffline"
                          :label="$t('user.registration.isCourseOffline')"
                          color="primary"
                          hide-details
                        ></v-checkbox>
                      </v-col>
                    </v-row>

                    <v-row>
                      <v-col cols="12" v-if="registerErr">
                        <p class="pa-0 ma-0 error--text">
                          {{ registerErr }}
                        </p>
                      </v-col>
                      <v-col cols="12">
                        <v-btn
                          depressed
                          block
                          color="primary"
                          style="text-transform: unset"
                          class="rounded-lg font-weight-normal"
                          :loading="btnLoading"
                          @click="register"
                        >
                          {{ $t("user.registration.register") }}
                        </v-btn>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </ValidationObserver>
            </v-col>
            <v-col
              cols="12"
              md="12"
              v-if="createOrUpdateOrganization"
              order="1"
              order-md="2"
            >
              <create-or-update-organization
                :isCreate="isCreate"
                :organizationData="editData"
                @close="createOrUpdateOrganization = null"
                @onOrganizationCreate="afterCreate"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>
  </div>
</template>
<script>
import "../../../core-module/util/validationRules";
import { hasPermission } from "@/app/user-module/helpers/authHelper";
import CreateOrUpdateOrganization from "../../../organization-module/components/CreateOrUpdateOrganization.vue";
import { createTrainingRegistration } from "@/app/course-module/methods/createTrainingRegistration";

export default {
  data() {
    return {
      course: null,
      certificateValidity: null,
      trainer: null,
      coursesData: [],
      organization: this.employeeOrganization,
      organizationsData: [],
      courseDuration: null,
      startDate: "",
      startDateMenu: false,
      endDate: "",
      date1: new Date(Date.now() - new Date().getTimezoneOffset() * 60000)
        .toISOString()
        .substr(0, 10),

      menu1: false,
      courseRate: null,
      discount: null,
      invoiceAmount: null,
      remarks: null,
      defaultPrice: 0,
      btnLoading: false,
      registerErr: false,
      hideAddBtn: false,
      createOrUpdateOrganization: false,
      isCreate: true,
      editData: {},
      trainerId: null,
      trainers: [],
      isCourseOffline: true,
      certificateValidityPrefix: "",
    };
  },
  components: {
    CreateOrUpdateOrganization,
  },
  props: {
    employee: {
      type: String,
      required: true,
    },
    employeeOrganization: String,
  },
  computed: {
    selectedOrganizationInfo() {
      return this.organizationsData.find(obj => obj._id === this.organization);
    },
    selectedOrganizationTrainingValidities() {
      return this.selectedOrganizationInfo?.trainingValidities ?? [];
    },
    selectedCourseInfo() {
      return this.coursesData.find(obj => obj._id === this.course);
    },
    selectedCourseCertificateValidity() {
      if (this.selectedOrganizationTrainingValidities.length) {
        const courseInfo = this.selectedOrganizationTrainingValidities.find(
          obj => obj.training?._id === this.course
        );

        if (courseInfo?.certificateValidity) {
          return courseInfo.certificateValidity;
        }
      }

      return this.selectedCourseInfo?.certificateValidity;
    },
    formattedStartDate: {
      get() {
        return this.startDate
          ? this.$moment(this.startDate).format("DD/MM/YYYY")
          : "";
      },
      set(value) {
        this.startDate = value;
      },
    },
    dialog: {
      get() {
        return {};
      },
      set(value) {
        if (!value) {
          this.$emit("close");
        }
      },
    },
    havePermissionCreateOrganization() {
      return hasPermission({
        currentRole: this.$store.state.authenticatedUserRole,
        currentPermissions: this.$store.state.authenticatedUserPermissions,
        requiredPermission: ["CREATE_ORGANIZATION"],
        restrictOrganizationManager:
          this.$store.state.authenticatedUser?.isOrganizationManager,
      });
    },
  },
  apollo: {
    getTrainings() {
      return {
        query: require("../../../course-module/api/query/getTrainings.gql"),
        variables: {
          limit: 0,
          skip: 0,
          filterInput: {
            approvalStatus: "APPROVED",
          },
        },
        result({ data, loading }) {
          if (data && data.getTrainings && data.getTrainings.trainings) {
            this.coursesData = data.getTrainings.trainings?.filter(
              x => x.isActive === true
            );
          }
        },
      };
    },
    getOrganizations() {
      return {
        query: require("../../../organization-module/api/query/getOrganizations.gql"),
        variables: { limit: 0, skip: 0, filterInput: null },
        result({ data, loading }) {
          if (
            data &&
            data.getOrganizations &&
            data.getOrganizations.organizations
          ) {
            this.organizationsData =
              data.getOrganizations.organizations?.filter(
                x => x.isActive === true
              );
          }
        },
      };
    },
    getSubRoles() {
      return {
        query: require("../../api/query/getSubRoles.gql"),
        variables: { limit: 0, skip: 0 },
        result({ data, loading }) {
          if (data && data.getSubRoles && data.getSubRoles.subRoles) {
            this.trainerId = data.getSubRoles.subRoles.find(
              x => x.name === "TRAINER"
            )?._id;
          }
        },
      };
    },
  },
  watch: {
    organization() {
      this.setCertificateValidity();
    },
    trainerId() {
      if (this.trainerId) {
        this.getTrainers();
      }
    },
    startDate() {
      if (this.courseDuration && this.startDate) this.setEndDate();
    },
  },
  methods: {
    getEmployeeText(item) {
      return `${item.name || ""} ${item.lastName || ""}`;
    },
    afterCreate(id) {
      this.$apollo.queries.getOrganizations.refetch({});
      this.organization = id ? id : null;
      this.createOrUpdateOrganization = false;
    },
    setCourseDuration() {
      this.courseDuration = this.selectedCourseInfo?.duration;

      this.defaultPrice = this.selectedCourseInfo?.price
        ? this.selectedCourseInfo.price
        : 0;

      if (this.courseDuration && this.startDate) this.setEndDate();

      this.setCertificateValidity();
    },
    setCertificateValidity() {
      if (this.selectedCourseCertificateValidity) {
        this.certificateValidity = this.selectedCourseCertificateValidity;
        this.certificateValidityPrefix = `${this.selectedCourseCertificateValidity} | `;
      }
    },
    setEndDate() {
      this.endDate = this.$moment(this.startDate)
        .add(this.courseDuration, "days")
        .subtract(1, "days")
        .format("DD/MM/YYYY");
    },
    formatDateServer(date) {
      if (!date) return null;

      const [day, month, year] = date.split("/");
      return `${year}-${month}-${day}`;
    },
    async getTrainers() {
      if (this.trainerId) {
        await this.$apollo
          .query({
            query: require("../../api/query/getEmployees.gql"),
            variables: {
              limit: 0,
              skip: 0,
              filterInput: {
                subRole: this.trainerId,
              },
            },
          })
          .then(result => {
            if (result?.data?.getEmployees?.employees) {
              this.trainers = result.data.getEmployees.employees.map(
                employee => {
                  let obj = {};
                  obj._id = employee._id;
                  obj.name = employee?.user?.firstName;
                  obj.lastName = employee?.user?.lastName;
                  obj.email = employee?.user?.email;
                  return obj;
                }
              );
            }
          });
      }
    },
    async register() {
      const isValid = await this.$refs.observer.validate();
      if (isValid) {
        this.btnLoading = true;
        let inputData = {
          input: {
            employee: this.employee,
            organization: this.organization,
            training: this.course,
            trainingDuration: this.courseDuration,
            certificateValidity: this.certificateValidity,
            trainer: this.trainer && this.isCourseOffline ? this.trainer : null,
            startDate: this.startDate,
            endDate: this.formatDateServer(this.endDate),
            unitPrice: this.defaultPrice,
            customPrice:
              this.courseRate != null
                ? this.getFloat(this.courseRate)
                : this.defaultPrice,
            remarks: this.remarks,
            trainingMode: this.isCourseOffline ? "OFFLINE" : "ONLINE",
          },
        };
        await createTrainingRegistration(inputData).then(result => {
          this.btnLoading = false;
          if (result.error) {
            if (result.error.includes("ALREADY_EXIST"))
              this.registerErr = this.$t("inputRules.emailAlreadyExist");
            // else if (result.error.includes("FIELD_REQUIRED"))
            //   this.registerErr = this.$t("emailOrCompanyEmailNeeded");
            else if (result.error.includes("ORGANIZATION_MISMATCH_ERROR")) {
              this.registerErr = this.$t(
                "user.registration.organizationMismatchError"
              );
            } else this.registerErr = this.$t("somethingWentWrong");
          } else this.$emit("afterCreate");
        });
      }
    },
  },
};
</script>
